import { apiClient } from '@borradh-workspace/api-client';
import {
  type PublicSiteView,
  publicSiteViewSchema,
} from '@borradh-workspace/contracts';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  type PublicSiteImageInput,
  type PublicSiteIntent,
  buildPublicSitePayload,
} from './public-site-update.payload';
export function usePublicSiteUpdate(
  locationId: string,
  callbacks: {
    onSuccess: (site: PublicSiteView, intent: PublicSiteIntent) => void;
    onError: (error: Error, intent: PublicSiteIntent) => void;
  }
) {
  const update = useMutation({
    mutationFn: (intent: PublicSiteIntent) =>
      apiClient.post<PublicSiteView>(
        `public-sites/${encodeURIComponent(locationId)}/${intent.action}`,
        buildPublicSitePayload(intent),
        { schema: publicSiteViewSchema }
      ),
    ...callbacks,
  });
  const { mutate } = update;
  const change = useCallback(
    (request: string, operationKey: string, images?: PublicSiteImageInput[]) =>
      mutate({
        action: 'changes',
        request,
        operationKey,
        ...(images?.length ? { images } : {}),
      }),
    [mutate]
  );
  const publish = useCallback(
    (selectedVersion: string) => mutate({ action: 'publish', selectedVersion }),
    [mutate]
  );
  const retry = useCallback(() => mutate({ action: 'retry' }), [mutate]);
  const sync = useCallback(() => mutate({ action: 'sync' }), [mutate]);
  return { ...update, change, publish, retry, sync };
}
